import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const formatINR = (v) => `₹ ${Math.round(num(v)).toLocaleString('en-IN')}`;

// ₹1.25 Cr / ₹4.50 L / ₹92,400 — a headline has to be readable at a glance
const formatShort = (v) => {
  const n = Math.round(num(v));
  const a = Math.abs(n);
  if (a >= 10000000) return `₹ ${(n / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `₹ ${(n / 100000).toFixed(2)} L`;
  return formatINR(n);
};

// 1.25Cr / 4.50L / 92,400 — no currency mark, because the column header carries
// it. Saves ~14px a cell, which is a column and a half across this table.
const compactNum = (v) => {
  const n = Math.round(num(v));
  const a = Math.abs(n);
  if (a >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
  if (a >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString('en-IN');
};

const pct = (part, whole) => (num(whole) > 0 ? Math.min(100, (num(part) / num(whole)) * 100) : 0);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NUM = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

// 21 Aug 2026 — short enough for a table, unambiguous about the month
const fmtDay = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Which month an invoice belongs to. The date first; failing that the month in
 * the reference number, because "5C/JUL-26/R4985" says it plainly.
 */
const invoiceMonth = (inv) => {
  const d = dayStart(inv.inv_date ?? inv.invoice_date);
  if (d) return { y: d.getFullYear(), m: d.getMonth() };
  const ref = (inv.reference_no ?? inv.invoice_num ?? '').toString().toUpperCase();
  const hit = ref.match(/(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[^0-9]?(\d{2})/);
  if (!hit) return null;
  return { y: 2000 + Number(hit[2]), m: MONTH_NUM[hit[1]] };
};

const monthKey = (p) => (p ? `${p.y}-${String(p.m + 1).padStart(2, '0')}` : 'unknown');
const monthLabel = (p) => (p ? `${MONTHS[p.m]} ${p.y}` : 'Month not recorded');

/* ── ageing ── */
const AGE_BUCKETS = [
  { key: 'd0_30', label: '0 – 30 days', color: '#15803d', bg: '#ecfdf5', border: '#16a34a' },
  { key: 'd31_60', label: '31 – 60 days', color: '#a16207', bg: '#fefce8', border: '#eab308' },
  { key: 'd61_90', label: '61 – 90 days', color: '#c2410c', bg: '#fff7ed', border: '#ea580c' },
  { key: 'd90p', label: 'over 90 days', color: '#b91c1c', bg: '#fef2f2', border: '#dc2626' },
];

const emptyAge = () => ({ d0_30: 0, d31_60: 0, d61_90: 0, d90p: 0 });

const bucketFor = (days) => {
  if (days <= 30) return 'd0_30';
  if (days <= 60) return 'd31_60';
  if (days <= 90) return 'd61_90';
  return 'd90p';
};

/**
 * Local midnight for a date value.
 *
 * `new Date('2026-06-02')` is parsed as UTC midnight, which in IST is 05:30 the
 * same morning. Measured against a local "now" of 18:11 that is 90 days plus
 * twelve hours, and rounding turned it into 91 — so every invoice dated the 1st
 * of a month was ageing into the next band a day early, and a 1 Aug invoice was
 * showing as 31 days instead of 30.
 */
const dayStart = (value) => {
  const raw = (value ?? '').toString().trim();
  if (!raw) return null;
  // A DATE-ONLY string has no timezone to honour, and letting Date parse it would
  // read it as UTC midnight — a day early west of Greenwich. Take it literally.
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  // Anything with a time is a real instant. Convert it to the LOCAL day, because
  // that is the day the money arrived here: 2026-08-31T18:30Z is 1 Sep in IST.
  // Reading the leading characters instead put such a credit under 31 Aug while
  // the row itself displayed 01 Sep.
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * When an invoice falls due: the first day of the month AFTER the invoice month.
 * A month's billing is payable from the 1st of the following month, so the clock
 * starts there — the day within the month does not matter, which also means a
 * missing inv_date costs nothing as long as the month is known.
 */
const dueDateOf = (inv) => {
  const p = invoiceMonth(inv);
  if (!p) return null;
  return new Date(p.y, p.m + 1, 1); // rolls the year over on its own
};

/**
 * An invoice whose month cannot be determined is aged into the oldest band, not
 * the newest. Treating an unknown as current would flatter the book and hide the
 * very rows most likely to be stale.
 */
const UNKNOWN_AGE_DAYS = 9999;

/** Whole days past due, midnight to midnight, so the bands are exact. */
const daysOutstanding = (inv, today) => {
  const start = dueDateOf(inv);
  if (!start) return UNKNOWN_AGE_DAYS;
  const midnightToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.round((midnightToday - start) / 86400000));
};

/* ── client categories ──
 * The feed may name this column any of several ways. Values are normalised so
 * "ELITE", "Elite Client" and "elite" all land in the same bucket.
 */
const TIER_FIELDS = [
  'client_type', 'client_category', 'client_tier', 'category', 'tier', 'segment',
  'client_segment', 'clienttype', 'group_type', 'client_group_type',
];

const TIERS = [
  { key: 'ELITE', label: 'Elite', short: 'Elite', color: '#6d28d9', bg: '#f5f3ff', border: '#ddd6fe' },
  { key: 'KEY', label: 'Key', short: 'Key', color: '#1e40af', bg: '#eef2ff', border: '#c7d2fe' },
  { key: 'GENERAL', label: 'General', short: 'Gen', color: '#0f766e', bg: '#ecfeff', border: '#a5f3fc' },
  { key: 'INCUBATION', label: 'Incubation', short: 'Incub', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  { key: 'TENDER', label: 'Tender', short: 'Tender', color: '#be123c', bg: '#fff1f2', border: '#fecdd3' },
  { key: 'UNSET', label: 'Not categorised', short: 'Other', color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
];

const TIER_BY_KEY = TIERS.reduce((m, t) => ({ ...m, [t.key]: t }), {});

const tierOf = (row) => {
  for (const f of TIER_FIELDS) {
    const raw = (row?.[f] ?? '').toString().trim().toUpperCase();
    if (!raw) continue;
    if (raw.includes('ELITE')) return 'ELITE';
    if (raw.includes('INCUBAT')) return 'INCUBATION';
    if (raw.includes('TENDER')) return 'TENDER';
    if (raw.includes('KEY')) return 'KEY';
    if (raw.includes('GENERAL') || raw.includes('DEFAULT')) return 'GENERAL';
    return 'UNSET';
  }
  return 'UNSET';
};

/**
 * Is this payment inside the range? The mapped-payments endpoint may filter on the
 * mapping's created_at rather than the payment date, so the range is enforced here
 * as well. Date-only, both ends inclusive; an unreadable date is kept.
 */
const inRange = (value, from, to) => {
  if (!from && !to) return true;
  const raw = (value ?? '').toString();
  if (!raw) return true;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return true;
  const day = iso(d);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

/* ── which bank the money came through ── */
const BANK_TYPES = ['AXIS', 'ICICI'];

const bankOf = (row) => {
  const declared = (row?.bank_type ?? '').toString().trim().toUpperCase();
  if (BANK_TYPES.includes(declared)) return declared;
  return (row?.trx_id ?? '').toString().trim().toUpperCase().startsWith('AXIS') ? 'AXIS' : 'ICICI';
};

// NEFT / RTGS / IMPS / UPI / cheque — as the bank labelled it
const modeOf = (row) => {
  const raw = (row?.trx_mode ?? row?.payment_mode ?? '').toString().trim().toUpperCase();
  return raw || 'NOT RECORDED';
};

const dayKeyOf = (row) => {
  const d = dayStart(row?.trx_date ?? row?.payment_date ?? row?.created_at);
  return d ? iso(d) : 'NOT RECORDED';
};

const WEEKEND = [0, 6];

const dayStyle = (key) => {
  if (key === 'NOT RECORDED') return { label: 'Not recorded', color: '#64748b', bg: '#f8fafc' };
  const d = dayStart(key);
  if (!d) return { label: key, color: '#475569', bg: '#f8fafc' };
  const weekend = WEEKEND.includes(d.getDay());
  return {
    // "Mon 01 Sep" — the weekday is what makes a collection pattern readable
    label: `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]} ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]}`,
    color: weekend ? '#9d174d' : '#334155',
    bg: weekend ? '#fdf2f8' : '#f1f5f9',
  };
};

const MODE_COLOURS = ['#1e40af', '#0f766e', '#6d28d9', '#b45309', '#9d174d', '#475569'];

const modeStyle = (key) => {
  if (key === 'NOT RECORDED') return { label: 'Not recorded', color: '#64748b', bg: '#f8fafc' };
  // a stable colour per name, so a mode keeps its colour between loads
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) % 997;
  const color = MODE_COLOURS[h % MODE_COLOURS.length];
  return { label: key, color, bg: `${color}14` };
};

const BANK_STYLE = {
  AXIS: { label: 'Axis Bank', color: '#9d174d', bg: '#fdf2f8' },
  ICICI: { label: 'ICICI Bank', color: '#c2410c', bg: '#fff7ed' },
};

const bankStyle = (key) => BANK_STYLE[key] || { label: key, color: '#475569', bg: '#f8fafc' };

/* ── payments not yet applied to invoices ── */
const BACKLOG = [
  { key: 'UNMAPPED', label: 'Unmapped', color: '#b91c1c', bg: '#fef2f2', border: '#dc2626' },
  { key: 'PARTIAL', label: 'Part mapped', color: '#a16207', bg: '#fefce8', border: '#eab308' },
  { key: 'SUSPENSE', label: 'Suspense', color: '#c2410c', bg: '#fff7ed', border: '#ea580c' },
  { key: 'OTHER', label: 'Other', color: '#6d28d9', bg: '#f5f3ff', border: '#7c3aed' },
];

const BACKLOG_BY_KEY = BACKLOG.reduce((m, b) => ({ ...m, [b.key]: b }), {});

/**
 * Where a bank credit stands. Parked and non-invoice bookings are states of their
 * own — they are not "unmapped waiting to be matched", they have been decided.
 */
const backlogStateOf = (row) => {
  const status = (row.mapping_status ?? '').toString().toLowerCase().trim();
  const other = (row.other_status ?? '').toString().toLowerCase().trim();
  if (status === 'suspense' || other === 'suspense') return 'SUSPENSE';
  if (status === 'other' || (other && other !== 'suspense')) return 'OTHER';
  const amount = num(row.amt ?? row.amount ?? row.trx_amt);
  const mapped = num(row.mapped_amount ?? row.mapped_amt);
  if (amount > 0 && mapped >= amount) return 'MAPPED';
  if (mapped > 0) return 'PARTIAL';
  return 'UNMAPPED';
};

const rowsFromBody = (body) => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return [];
  for (const k of ['data', 'results', 'result', 'rows', 'records', 'payments']) {
    if (Array.isArray(body[k])) return body[k];
  }
  const any = Object.values(body).find((v) => Array.isArray(v));
  return Array.isArray(any) ? any : [];
};

/* ── small presentational pieces ── */

/** A small square tile. Uniform size, so a strip of them reads as one block. */
function StatCard({ label, value, sub, palette, onClick }) {
  const Icon = palette.Icon;
  return (
    <Box
      onClick={onClick}
      title={onClick ? 'Click for the detail' : undefined}
      sx={{
        backgroundColor: palette.bg,
        borderRadius: '8px',
        border: `1px solid ${palette.border}33`,
        borderTop: `3px solid ${palette.border}`,
        px: 1,
        py: 0.65,
        minHeight: 56,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { filter: 'brightness(0.96)' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        {Icon && <Icon sx={{ fontSize: 13, color: palette.color }} />}
        <Typography
          noWrap
          sx={{ fontSize: '9px', color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: palette.color, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && <Typography noWrap sx={{ fontSize: '9.5px', color: '#64748b' }}>{sub}</Typography>}
    </Box>
  );
}

/** Elite / Key / General / Incubation, in the category's own colour. */
function TierChip({ tier }) {
  const t = TIER_BY_KEY[tier] || TIER_BY_KEY.UNSET;
  return (
    <Chip
      size="small"
      label={t.key === 'UNSET' ? 'Not set' : t.label}
      sx={{
        height: 17,
        fontSize: '9px',
        fontWeight: 700,
        backgroundColor: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
      }}
    />
  );
}

/**
 * One titled block. Every section of the dashboard is one of these, so the page
 * reads as a set of panels rather than a run of tables.
 */
function Panel({ title, hint, right, children, pad = true }) {
  return (
    <Paper
      sx={{
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.1,
          py: 0.6,
          backgroundColor: '#f8faff',
          borderBottom: '1px solid #e2e8f0',
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          {title}
        </Typography>
        {hint && <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{hint}</Typography>}
        {right && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>{right}</Box>}
      </Box>
      <Box sx={{ p: pad ? 0.75 : 0, minWidth: 0 }}>{children}</Box>
    </Paper>
  );
}

/**
 * The receipt split as a table — the same shape whether the rows are banks or
 * payment modes, so both panels stay identical and half-width.
 */
function SplitTable({ label, rows, total, styleOf, onDrill, maxHeight = 268, plainLabel = false }) {
  if (rows.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
        No payments in this period.
      </Box>
    );
  }
  const cols = [
    { key: 'total', head: 'Collected', colour: '#15803d', state: null },
    { key: 'mapped', head: 'Mapped', colour: '#1e40af', state: 'mapped' },
    { key: 'unmapped', head: 'Unmapped', colour: '#b91c1c', state: 'unmapped' },
    { key: 'suspense', head: 'Susp', colour: '#c2410c', state: 'suspense' },
    { key: 'other', head: 'Other', colour: '#6d28d9', state: 'other' },
  ];
  const sum = (k) => rows.reduce((t, r) => t + r[k], 0);
  return (
    <TableContainer sx={{ maxHeight, overflow: 'auto' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...SUBHEAD_SX, minWidth: 96 }}>{label}</TableCell>
            {cols.map((c) => (
              <TableCell key={c.key} sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}>
                {c.head}
              </TableCell>
            ))}
            <TableCell sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }} title="Bank credits received">
              Pmts
            </TableCell>
            <TableCell
              sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}
              title="Clients identified so far — a credit has no client until it is mapped"
            >
              Clients
            </TableCell>
            <TableCell
              sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}
              title="Share of the period's collection"
            >
              Share
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const st = styleOf(r.key);
            return (
              <TableRow key={r.key} sx={{ backgroundColor: i % 2 ? '#f8faff' : '#fff' }}>
                <TableCell sx={{ padding: '2px 8px' }}>
                  {plainLabel ? (
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: st.color, whiteSpace: 'nowrap' }}>
                      {st.label}
                    </Typography>
                  ) : (
                    <Chip
                      size="small"
                      label={st.label}
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        fontWeight: 700,
                        backgroundColor: st.bg,
                        color: st.color,
                        border: `1px solid ${st.color}44`,
                        maxWidth: 130,
                      }}
                    />
                  )}
                </TableCell>
                {cols.map((c) => (
                  <TableCell
                    key={c.key}
                    onClick={r[c.key] > 0 ? () => onDrill(r, c.state, st.label) : undefined}
                    title={
                      r[c.key] > 0
                        ? `${formatINR(r[c.key])} — click for the payments`
                        : undefined
                    }
                    sx={{
                      ...NUM_SX,
                      fontSize: '11.5px',
                      padding: '2px 6px',
                      fontWeight: 700,
                      color: r[c.key] > 0 ? c.colour : '#dbe2ea',
                      cursor: r[c.key] > 0 ? 'pointer' : 'default',
                      '&:hover': r[c.key] > 0 ? { backgroundColor: '#dbeafe' } : {},
                    }}
                  >
                    {r[c.key] > 0 ? compactNum(r[c.key]) : '—'}
                  </TableCell>
                ))}
                <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', padding: '2px 6px', color: '#475569' }}>
                  {r.count}
                </TableCell>
                <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', padding: '2px 6px', color: r.clients.size ? '#475569' : '#dbe2ea' }}>
                  {r.clients.size || '—'}
                </TableCell>
                <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', padding: '2px 6px', color: st.color, fontWeight: 700 }}>
                  {Math.round(pct(r.total, total))}%
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        {rows.length > 1 && (
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...FOOT_SX, padding: '5px 8px' }}>All</TableCell>
              {cols.map((c) => (
                <TableCell key={c.key} sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right', color: c.colour }}>
                  {sum(c.key) > 0 ? compactNum(sum(c.key)) : '—'}
                </TableCell>
              ))}
              <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right' }}>{sum('count')}</TableCell>
              <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right' }}>
                {new Set(rows.flatMap((r) => Array.from(r.clients))).size || '—'}
              </TableCell>
              <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right' }}>
                {Math.round(pct(sum('total'), total))}%
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
}

const HEADER_SX = {
  backgroundColor: '#1e40af',
  color: '#fff',
  fontWeight: 700,
  fontSize: '10.5px',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  padding: '7px 10px',
  // sticky header cells carry their own background from MUI; without this they
  // paint white and the header disappears
  '&.MuiTableCell-stickyHeader': { backgroundColor: '#1e40af', color: '#fff' },
};

const SUBHEAD_SX = {
  backgroundColor: '#334155',
  color: '#fff',
  fontWeight: 700,
  fontSize: '9.5px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  padding: '5px 10px',
  borderBottom: 'none',
  '&.MuiTableCell-stickyHeader': { backgroundColor: '#334155', color: '#fff' },
};

// the three parent column groups, each split across the client categories
const GROUPS = [
  { key: 'received', label: 'Received', color: '#15803d', head: '#166534', split: true },
  { key: 'tds', label: 'TDS', color: '#6d28d9', head: '#5b21b6', split: false },
  { key: 'due', label: 'Due', color: '#c2410c', head: '#9a3412', split: true },
];

// the pinned totals row under a scrolling table
const FOOT_SX = {
  backgroundColor: '#e8edf5',
  color: '#1e293b',
  fontWeight: 800,
  fontSize: '11px',
  whiteSpace: 'nowrap',
  padding: '5px 10px',
  borderTop: '2px solid #cbd5e1',
  position: 'sticky',
  bottom: 0,
  '&.MuiTableCell-stickyHeader': { backgroundColor: '#e8edf5', color: '#1e293b' },
};

const NUM_SX = { fontSize: '12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

export default function CollectionDashboard({ from, to, refreshSignal }) {
  const [invoices, setInvoices] = useState([]);
  const [collections, setCollections] = useState([]); // mapped lines in the period
  const [payments, setPayments] = useState([]); // bank credits in the period
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null); // { title, subtitle, rows } — the due breakdown popup

  const today = useMemo(() => new Date(), []);

  /**
   * The period follows the tabs' shared date range. When that range happens to be
   * exactly one calendar month it is named as such ("Aug 2026"); otherwise the
   * dates themselves are the label, because "so far this month" would be a lie.
   */
  const period = useMemo(() => {
    const now = new Date();
    const fallbackStart = iso(new Date(now.getFullYear(), now.getMonth(), 1));
    const fallbackEnd = iso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const start = from || fallbackStart;
    const end = to || fallbackEnd;

    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    const valid = !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime());
    const lastOfMonth = valid ? new Date(s.getFullYear(), s.getMonth() + 1, 0).getDate() : 0;
    const wholeMonth =
      valid &&
      s.getDate() === 1 &&
      s.getMonth() === e.getMonth() &&
      s.getFullYear() === e.getFullYear() &&
      e.getDate() === lastOfMonth;
    const isCurrentMonth =
      valid && s.getMonth() === now.getMonth() && s.getFullYear() === now.getFullYear();

    return {
      start,
      end,
      label: wholeMonth && valid ? `${MONTHS[s.getMonth()]} ${s.getFullYear()}` : `${start} to ${end}`,
      title: wholeMonth && isCurrentMonth ? 'Collection so far' : 'Collection',
      note: wholeMonth && isCurrentMonth ? `day ${now.getDate()} of ${lastOfMonth}` : '',
    };
  }, [from, to]);

  const load = async () => {
    setLoading(true);
    setError(null);
    const dueApi = process.env.REACT_APP_GET_OVERALL_DUE;
    const mappedApi = process.env.REACT_APP_GET_PAYMENT_MAPPING_DETAILS;
    const paymentsApi = process.env.REACT_APP_CLIENT_PAYMENT_DETAILS;
    const range = { start_date: period.start, end_date: period.end };
    try {
      const [dueRes, mapRes, payRes] = await Promise.all([
        dueApi ? axios.get(dueApi, { withCredentials: true }) : Promise.resolve(null),
        mappedApi ? axios.get(mappedApi, { params: range, withCredentials: true }) : Promise.resolve(null),
        paymentsApi ? axios.get(paymentsApi, { params: range, withCredentials: true }) : Promise.resolve(null),
      ]);
      setInvoices(rowsFromBody(dueRes?.data));
      setCollections(
        rowsFromBody(mapRes?.data).filter((l) =>
          inRange(l.trx_date ?? l.payment_date ?? l.created_at, period.start, period.end)
        )
      );
      setPayments(
        rowsFromBody(payRes?.data).filter((r) =>
          inRange(r.trx_date ?? r.payment_date ?? r.created_at, period.start, period.end)
        )
      );
      if (!dueApi) setError('API not configured (REACT_APP_GET_OVERALL_DUE)');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not load the dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal, period.start, period.end]);

  /* ── one row per invoice, with its age ── */
  const invoiceRows = useMemo(() => {
    const byRef = new Map();
    invoices.forEach((r) => {
      const ref = r.reference_no ?? r.invoice_num ?? r.invoice_number;
      if (!ref) return;
      const key = String(ref);
      if (byRef.has(key)) return;
      const row = {
        ...r,
        reference_no: ref,
        invoice_amount: num(r.invoice_amount ?? r.invoice_amt ?? r.inv_amount),
        prev_payment: num(r.prev_payment ?? r.previous_payment ?? r.paid_amount),
        credit: num(r.credit ?? r.credit_amount ?? r.creditnote_amt),
        debit: num(r.debit ?? r.debit_amount ?? r.debitnote_amt),
        due: num(r.due ?? r.due_amount ?? r.outstanding ?? r.balance),
      };
      row._period = invoiceMonth(row);
      row._monthKey = monthKey(row._period);
      row._days = daysOutstanding(row, today);
      row._bucket = bucketFor(row._days);
      byRef.set(key, row);
    });
    return Array.from(byRef.values());
  }, [invoices, today]);

  const openInvoices = useMemo(() => invoiceRows.filter((r) => num(r.due) > 0), [invoiceRows]);

  /* ── who did the mapping this period, and how much ── */
  const mappedBy = useMemo(() => {
    const byUser = new Map();
    collections.forEach((l) => {
      const who = (l.created_by ?? '').toString().trim() || 'not recorded';
      const type = (l.mapping_type ?? '').toString().toLowerCase();
      if (!byUser.has(who)) {
        byUser.set(who, { who, amount: 0, tds: 0, lines: 0, trx: new Set(), clients: new Set(), auto: 0, manual: 0 });
      }
      const u = byUser.get(who);
      u.amount += num(l.amt ?? l.allocated ?? l.amount);
      u.tds += num(l.tds_amt ?? l.tds_amount);
      u.lines += 1;
      if (l.trx_id) u.trx.add(l.trx_id);
      const c = l.client_fk ?? l.client_id ?? l.client_name;
      if (c != null && c !== '') u.clients.add(String(c));
      if (type.includes('auto')) u.auto += 1;
      else u.manual += 1;
    });
    return Array.from(byUser.values()).sort((a, b) => b.amount - a.amount);
  }, [collections]);

  /**
   * Every rupee that arrived in the period, split by where it stands. The four
   * parts add back to the total exactly:
   *
   *   mapped + unmapped + suspense + other = total collected
   *
   * A part-mapped credit contributes to both mapped and unmapped — its money is
   * genuinely split — which is why the parts are amounts, not payment counts.
   */
  const receipts = useMemo(() => {
    const out = {
      total: 0,
      mapped: 0,
      unmapped: 0,
      suspense: 0,
      other: 0,
      count: 0,
      rows: { all: [], mapped: [], unmapped: [], suspense: [], other: [] },
    };
    payments.forEach((r) => {
      const state = backlogStateOf(r);
      const amount = num(r.amt ?? r.amount ?? r.trx_amt);
      const mapped = Math.min(amount, num(r.mapped_amount ?? r.mapped_amt));
      const row = { ...r, _state: state, _amount: amount, _mapped: mapped };
      out.total += amount;
      out.count += 1;
      out.rows.all.push(row);
      if (state === 'SUSPENSE') {
        out.suspense += amount;
        out.rows.suspense.push(row);
        return;
      }
      if (state === 'OTHER') {
        out.other += amount;
        out.rows.other.push(row);
        return;
      }
      if (mapped > 0) {
        out.mapped += mapped;
        out.rows.mapped.push(row);
      }
      if (amount - mapped > 0) {
        out.unmapped += amount - mapped;
        out.rows.unmapped.push(row);
      }
    });
    return out;
  }, [payments]);

  /**
   * The receipt split grouped by whatever key the caller picks — the bank a credit
   * came through, or the mode it came by. Built off the payments feed so the rows
   * add back to the KPI totals exactly.
   */
  const splitBy = useCallback(
    (keyOf, unknownKey) => {
      const map = new Map();
      const blank = (key) => ({
        key,
        total: 0,
        mapped: 0,
        unmapped: 0,
        suspense: 0,
        other: 0,
        count: 0,
        clients: new Set(),
        rows: [],
      });
      payments.forEach((r) => {
        const key = keyOf(r);
        if (!map.has(key)) map.set(key, blank(key));
        const b = map.get(key);
        const state = backlogStateOf(r);
        const amount = num(r.amt ?? r.amount ?? r.trx_amt);
        const mapped = Math.min(amount, num(r.mapped_amount ?? r.mapped_amt));
        b.total += amount;
        b.count += 1;
        b.rows.push({ ...r, _state: state, _amount: amount, _mapped: mapped });
        if (state === 'SUSPENSE') b.suspense += amount;
        else if (state === 'OTHER') b.other += amount;
        else {
          b.mapped += mapped;
          b.unmapped += Math.max(0, amount - mapped);
        }
      });
      // A bank credit carries no client until it is mapped, so the payments feed
      // has none to count. The clients come from the mapped lines instead.
      collections.forEach((l) => {
        const key = keyOf(l);
        // If the mapped line does not carry this grouping field, attributing its
        // client anywhere is a guess — it was landing every client under
        // "Not recorded", which read as 515 clients against 0 payments.
        if (unknownKey && key === unknownKey) return;
        if (!map.has(key)) map.set(key, blank(key));
        const client = String(l.client_fk ?? l.client_id ?? l.client_name ?? '');
        if (client) map.get(key).clients.add(client);
      });
      // a row with nothing in it is noise, not information
      return Array.from(map.values())
        .filter((b) => b.total > 0 || b.count > 0 || b.clients.size > 0)
        .sort((a, b) => b.total - a.total);
    },
    [payments, collections]
  );

  const byBank = useMemo(() => splitBy(bankOf), [splitBy]);
  const byMode = useMemo(() => splitBy(modeOf, 'NOT RECORDED'), [splitBy]);
  const byDay = useMemo(
    () =>
      splitBy(dayKeyOf, 'NOT RECORDED').sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0)),
    [splitBy]
  );

  const drillSplit = (row, state, label) =>
    openTxns(
      `${label} — ${state || 'all payments'}`,
      period.label,
      state
        ? row.rows.filter((r) =>
            state === 'mapped'
              ? r._mapped > 0 && r._state !== 'SUSPENSE' && r._state !== 'OTHER'
              : state === 'unmapped'
                ? r._amount - r._mapped > 0 && r._state !== 'SUSPENSE' && r._state !== 'OTHER'
                : r._state === state.toUpperCase()
          )
        : row.rows
    );

  /**
   * Received and due per client group (the corporate group a client belongs to,
   * e.g. "Medanta Group"). The due feed is the only one that carries group_name,
   * so payments are attributed through a client -> group map built from it.
   */
  const groupOfClient = useMemo(() => {
    const m = new Map();
    invoiceRows.forEach((r) => {
      const k = String(r.client_id ?? r.client_fk ?? r.client_name ?? '');
      if (!k) return;
      const g = (r.group_name ?? '').toString().trim();
      if (g && !m.has(k)) m.set(k, g);
    });
    return m;
  }, [invoiceRows]);

  const byClientGroup = useMemo(() => {
    const map = new Map();
    const blank = (key) => ({
      key,
      received: 0,
      due: 0,
      clients: new Set(),
      owing: new Set(),
      invoices: 0,
      rows: [],
    });
    const bucket = (key) => {
      if (!map.has(key)) map.set(key, blank(key));
      return map.get(key);
    };

    invoiceRows.forEach((r) => {
      const g = (r.group_name ?? '').toString().trim();
      if (!g) return; // ungrouped clients are reported elsewhere, not as a group
      const b = bucket(g);
      const client = String(r.client_id ?? r.client_fk ?? r.client_name ?? '');
      b.due += num(r.due);
      if (client) b.clients.add(client);
      if (num(r.due) > 0) {
        b.invoices += 1;
        if (client) b.owing.add(client);
      }
    });

    collections.forEach((l) => {
      const client = String(l.client_fk ?? l.client_id ?? l.client_name ?? '');
      const g = (l.group_name ?? '').toString().trim() || groupOfClient.get(client) || '';
      if (!g) return; // no group to report it under
      const b = bucket(g);
      b.received += num(l.amt ?? l.allocated ?? l.amount);
      if (client) b.clients.add(client);
      b.rows.push(l);
    });

    return Array.from(map.values())
      .filter((b) => b.received > 0 || b.due > 0)
      .sort((a, b) => b.due - a.due || b.received - a.received);
  }, [invoiceRows, collections, groupOfClient]);

  const totalOutstanding = useMemo(
    () => invoiceRows.reduce((s, r) => s + num(r.due), 0),
    [invoiceRows]
  );

  /* ── ageing of the whole book ── */
  const ageTotals = useMemo(() => {
    const t = emptyAge();
    openInvoices.forEach((r) => {
      t[r._bucket] += num(r.due);
    });
    return t;
  }, [openInvoices]);

  /* ── invoice-month rollup ──
   * Received and TDS come from the mapped payment lines, the same source as the
   * headline figure, so the Received column adds up to Collected this month.
   * Due and the invoice counts come from the outstanding book. Both are split by
   * client category.
   */

  /**
   * client -> category. Built from BOTH feeds: whichever one carries the column
   * teaches the other. Without this, Received was categorised from the payment
   * lines while Due fell back to "Not set", so the two never lined up.
   */
  const tierByClient = useMemo(() => {
    const m = new Map();
    const learn = (row) => {
      const k = String(row.client_id ?? row.client_fk ?? row.client_name ?? '');
      if (!k) return;
      const t = tierOf(row);
      if (t !== 'UNSET' || !m.has(k)) m.set(k, t);
    };
    invoiceRows.forEach(learn);
    collections.forEach(learn);
    return m;
  }, [invoiceRows, collections]);

  const categoryOf = useCallback(
    (row) => {
      const own = tierOf(row);
      if (own !== 'UNSET') return own;
      const k = String(row.client_fk ?? row.client_id ?? row.client_name ?? '');
      return tierByClient.get(k) || 'UNSET';
    },
    [tierByClient]
  );

  const byMonth = useMemo(() => {
    const map = new Map();
    const blank = (key, period) => ({
      key,
      period,
      label: monthLabel(period),
      received: 0,
      tds: 0,
      due: 0,
      invoiced: 0,
      // clients who actually paid this month, and the invoices their money was
      // adjusted to — both counted off the payment lines, not the due book
      payClients: new Set(),
      paidInvoices: new Set(),
      // the due book's own counts, for the Due column
      invoices: 0,
      openInvoices: 0,
      byCat: new Map(),
    });
    const cat = (o, c) => {
      if (!o.byCat.has(c)) {
        o.byCat.set(c, {
          key: c,
          received: 0,
          tds: 0,
          due: 0,
          invoices: 0,
          clients: new Set(),
          paidInvoices: new Set(),
        });
      }
      return o.byCat.get(c);
    };

    // what came in, by the month of the invoice it settled
    collections.forEach((l) => {
      const p = invoiceMonth({ inv_date: l.inv_date, reference_no: l.invoice_num ?? l.reference_no });
      const k = monthKey(p);
      if (!map.has(k)) map.set(k, blank(k, p));
      const o = map.get(k);
      const c = categoryOf(l);
      const amt = num(l.amt ?? l.allocated ?? l.amount);
      const tds = num(l.tds_amt ?? l.tds_amount);
      const client = String(l.client_fk ?? l.client_id ?? l.client_name ?? '');
      o.received += amt;
      o.tds += tds;
      if (client) o.payClients.add(client);
      const invoice = String(l.invoice_num ?? l.reference_no ?? '');
      if (invoice) o.paidInvoices.add(invoice);
      const cc = cat(o, c);
      cc.received += amt;
      cc.tds += tds;
      if (client) cc.clients.add(client);
      if (invoice) cc.paidInvoices.add(invoice);
    });

    // what is still owed, from the outstanding book
    invoiceRows.forEach((r) => {
      const k = r._monthKey;
      if (!map.has(k)) map.set(k, blank(k, r._period));
      const o = map.get(k);
      const c = categoryOf(r);
      const client = String(r.client_id ?? r.client_fk ?? r.client_name ?? '');
      o.invoiced += num(r.invoice_amount);
      o.invoices += 1;
      o.due += num(r.due);
      const cc = cat(o, c);
      cc.due += num(r.due);
      cc.invoices += 1;
      if (client) cc.clients.add(client);
      if (num(r.due) > 0) o.openInvoices += 1;
    });

    const out = Array.from(map.values());
    out.forEach((o) => {
      o.cats = TIERS.map((t) => o.byCat.get(t.key))
        .filter(Boolean)
        .filter((c) => c.received > 0 || c.due > 0);
    });
    return out.sort((a, b) => {
      if (!a.period) return 1;
      if (!b.period) return -1;
      return b.period.y - a.period.y || b.period.m - a.period.m;
    });
  }, [invoiceRows, collections, categoryOf]);

  // Only the categories that appear anywhere get a column, so an unused one does
  // not cost width. Order follows TIERS: Elite, Key, General, Incubation, Not set.
  const shownCats = useMemo(() => {
    const seen = new Set();
    byMonth.forEach((m) =>
      m.byCat.forEach((c, k) => {
        if (c.received > 0 || c.tds > 0 || c.due > 0 || c.invoices > 0) seen.add(k);
      })
    );
    const list = TIERS.filter((t) => seen.has(t.key));
    return list.length ? list : TIERS.filter((t) => t.key !== 'UNSET');
  }, [byMonth]);

  const monthTotals = useMemo(
    () =>
      byMonth.reduce(
        (t, m) => ({
          received: t.received + m.received,
          tds: t.tds + m.tds,
          due: t.due + m.due,
          invoices: t.invoices + m.invoices,
          openInvoices: t.openInvoices + m.openInvoices,
          payClients: new Set([...t.payClients, ...m.payClients]),
          paidInvoices: t.paidInvoices + m.paidInvoices.size,
        }),
        { received: 0, tds: 0, due: 0, invoices: 0, openInvoices: 0, payClients: new Set(), paidInvoices: 0 }
      ),
    [byMonth]
  );

  const catTotals = useMemo(() => {
    const m = new Map();
    byMonth.forEach((mo) =>
      mo.byCat.forEach((c, k) => {
        if (!m.has(k)) m.set(k, { received: 0, tds: 0, due: 0 });
        const t = m.get(k);
        t.received += c.received;
        t.tds += c.tds;
        t.due += c.due;
      })
    );
    return m;
  }, [byMonth]);

  /* ── client categories ── */
  // biggest outstanding first — the chase order; age breaks a tie
  const byAmountThenAge = (a, b) => num(b.due) - num(a.due) || b._days - a._days;

  // a group statement reads better in invoice order: oldest first, then by client
  const byInvoiceDateThenClient = (a, b) => {
    const ad = a._period ? a._period.y * 12 + a._period.m : Number.MAX_SAFE_INTEGER;
    const bd = b._period ? b._period.y * 12 + b._period.m : Number.MAX_SAFE_INTEGER;
    if (ad !== bd) return ad - bd;
    const an = (a.client_name ?? '').toString();
    const bn = (b.client_name ?? '').toString();
    if (an !== bn) return an.localeCompare(bn);
    return (a.reference_no ?? '').toString().localeCompare((b.reference_no ?? '').toString());
  };

  const openDue = (title, subtitle, rows, sort = byAmountThenAge) =>
    setDetail({ kind: 'due', title, subtitle, rows: rows.slice().sort(sort) });

  // the payment lines behind a collected figure — what came in, and who mapped it
  const openPayments = (title, subtitle, lines) => {
    const mappers = new Set(lines.map((l) => (l.created_by ?? '').toString().trim()));
    setDetail({
      kind: 'payments',
      title,
      subtitle,
      // a single mapper across every line makes the column redundant
      showMappedBy: mappers.size > 1,
      rows: lines
        .slice()
        .sort((a, b) => num(b.amt ?? b.allocated ?? b.amount) - num(a.amt ?? a.allocated ?? a.amount)),
    });
  };

  // the bank credits behind a backlog tile
  const openTxns = (title, subtitle, rows) =>
    setDetail({
      kind: 'txns',
      title,
      subtitle,
      rows: rows.slice().sort((a, b) => b._amount - a._amount),
    });

  /**
   * Every number on the table goes through here, so clicking any of them shows
   * the records behind it. Received and TDS come from the payment lines, Due from
   * the outstanding book; `catKey` narrows to one client category.
   */
  const drill = (kind, m, catKey) => {
    const label = catKey ? `${m.label} · ${(TIER_BY_KEY[catKey] || TIER_BY_KEY.UNSET).label}` : m.label;
    if (kind === 'due') {
      const rows = invoiceRows.filter(
        (r) => r._monthKey === m.key && num(r.due) > 0 && (!catKey || categoryOf(r) === catKey)
      );
      openDue(`${label} — due`, `${rows.length} open invoice${rows.length === 1 ? '' : 's'}`, rows);
      return;
    }
    const lines = collections.filter(
      (l) =>
        monthKey(invoiceMonth({ inv_date: l.inv_date, reference_no: l.invoice_num ?? l.reference_no })) ===
          m.key && (!catKey || categoryOf(l) === catKey)
    );
    openPayments(
      `${label} — ${kind === 'tds' ? 'TDS withheld' : 'received'}`,
      `${lines.length} payment line${lines.length === 1 ? '' : 's'} in ${period.label}`,
      lines
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 1, p: 1, borderRadius: '6px', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '12px' }}>
          {error}
        </Box>
      )}

      <Box sx={{ mb: 0.6 }}>
      <Panel
        title={`${period.title} — ${period.label}`}
        hint={
          [
            period.note,
            receipts.total > 0
              ? `mapped + unmapped + suspense + others = total collected`
              : '',
          ]
            .filter(Boolean)
            .join(' · ')
        }
        right={
          <IconButton
            size="small"
            onClick={load}
            aria-label="Refresh"
            sx={{ color: '#1e40af', border: '1px solid #cbd5e1', borderRadius: '6px', p: 0.3 }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        }
      >
      <Box
        sx={{
          display: 'grid',
          // ten KPIs on one line: the receipt split, total due, then the ageing
          // bands. Scrolls sideways rather than wrapping onto a second row.
          gridTemplateColumns: 'repeat(10, minmax(112px, 1fr))',
          gap: 0.4,
          overflowX: 'auto',
          pb: 0.25,
        }}
      >
        <StatCard
          label="Total collected"
          value={formatShort(receipts.total)}
          sub={`${receipts.count} payment${receipts.count === 1 ? '' : 's'}`}
          palette={{ bg: '#ecfdf5', border: '#16a34a', color: '#15803d', Icon: TrendingUpIcon }}
          onClick={() => openTxns('Total collected', `${receipts.count} payments in ${period.label}`, receipts.rows.all)}
        />
        <StatCard
          label="Mapped"
          value={formatShort(receipts.mapped)}
          sub={`${Math.round(pct(receipts.mapped, receipts.total))}% of collection`}
          palette={{ bg: '#eef2ff', border: '#3b82f6', color: '#1e40af', Icon: AccountBalanceWalletIcon }}
          onClick={() => openTxns('Mapped', `applied to invoices in ${period.label}`, receipts.rows.mapped)}
        />
        <StatCard
          label="Unmapped"
          value={formatShort(receipts.unmapped)}
          sub={`${receipts.rows.unmapped.length} payment${receipts.rows.unmapped.length === 1 ? '' : 's'}`}
          palette={{ bg: '#fef2f2', border: '#dc2626', color: '#b91c1c', Icon: ErrorOutlineIcon }}
          onClick={() => openTxns('Unmapped', `still to apply, from ${period.label}`, receipts.rows.unmapped)}
        />
        <StatCard
          label="Suspense"
          value={formatShort(receipts.suspense)}
          sub={`${receipts.rows.suspense.length} parked`}
          palette={{ bg: '#fff7ed', border: '#ea580c', color: '#c2410c', Icon: ReceiptLongIcon }}
          onClick={() => openTxns('Suspense', `parked in ${period.label}`, receipts.rows.suspense)}
        />
        <StatCard
          label="Others"
          value={formatShort(receipts.other)}
          sub={`${receipts.rows.other.length} not invoice`}
          palette={{ bg: '#f5f3ff', border: '#7c3aed', color: '#6d28d9', Icon: ReceiptLongIcon }}
          onClick={() => openTxns('Others', `booked outside invoices in ${period.label}`, receipts.rows.other)}
        />
        <StatCard
          label="Total due"
          value={formatShort(totalOutstanding)}
          sub={`${openInvoices.length} open invoices`}
          palette={{ bg: '#fffbeb', border: '#b45309', color: '#b45309', Icon: ErrorOutlineIcon }}
          onClick={() => openDue('Total due', `${openInvoices.length} open invoices`, openInvoices)}
        />
        {AGE_BUCKETS.map((b) => (
          <Box
            key={b.key}
            onClick={() =>
              openDue(b.label, `outstanding ageing ${b.label}`, openInvoices.filter((r) => r._bucket === b.key))
            }
            title={`${formatINR(ageTotals[b.key])} — counted from the 1st of the month after the invoice · click for the invoices`}
            sx={{
              backgroundColor: b.bg,
              border: `1px solid ${b.border}33`,
              borderTop: `3px solid ${b.border}`,
              borderRadius: '8px',
              px: 1,
              py: 0.65,
              minHeight: 56,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': { filter: 'brightness(0.96)' },
            }}
          >
            <Typography noWrap sx={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Due {b.label}
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: b.color, lineHeight: 1.1 }}>
              {formatShort(ageTotals[b.key])}
            </Typography>
            <Typography noWrap sx={{ fontSize: '9.5px', color: '#64748b' }}>
              {Math.round(pct(ageTotals[b.key], totalOutstanding))}% of book
            </Typography>
          </Box>
        ))}
      </Box>
      </Panel>
      </Box>

      {/* ── invoice month level ── */}
      <Box sx={{ mb: 0.6 }}>
      <Panel
        title="Collection by invoice month"
        hint="₹ in K / L / Cr"
        pad={false}
      >
      <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            {/* Two clean bands: one blue row for the groups, one slate row for the
                categories. Colour is a thin accent, not a fill — four dark fills
                in one header read as a mess. */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  ...HEADER_SX,
                  minWidth: 112,
                  verticalAlign: 'middle',
                  position: 'sticky',
                  left: 0,
                  zIndex: 4,
                  '&.MuiTableCell-stickyHeader': { backgroundColor: '#1e40af', color: '#fff' },
                }}
              >
                Invoice Month
              </TableCell>
              {GROUPS.map((g) =>
                g.split ? (
                  <TableCell
                    key={g.key}
                    colSpan={shownCats.length + 1}
                    align="center"
                    sx={{
                      ...HEADER_SX,
                      borderLeft: '1px solid #ffffff44',
                      borderBottom: `3px solid ${g.color}`,
                      '&.MuiTableCell-stickyHeader': { backgroundColor: '#1e40af', color: '#fff' },
                    }}
                  >
                    {g.label} (₹)
                  </TableCell>
                ) : (
                  <TableCell
                    key={g.key}
                    rowSpan={2}
                    align="right"
                    sx={{
                      ...HEADER_SX,
                      verticalAlign: 'middle',
                      borderLeft: '1px solid #ffffff44',
                      borderBottom: `3px solid ${g.color}`,
                      '&.MuiTableCell-stickyHeader': { backgroundColor: '#1e40af', color: '#fff' },
                    }}
                  >
                    {g.label} (₹)
                  </TableCell>
                )
              )}
              <TableCell
                rowSpan={2}
                align="right"
                title="Clients who paid this month"
                sx={{ ...HEADER_SX, verticalAlign: 'middle', padding: '7px 6px', borderLeft: '1px solid #ffffff44' }}
              >
                Clients
              </TableCell>
              <TableCell
                rowSpan={2}
                align="right"
                title="Invoices those payments were adjusted to"
                sx={{ ...HEADER_SX, verticalAlign: 'middle', padding: '7px 6px' }}
              >
                Invoices
              </TableCell>
            </TableRow>
            <TableRow>
              {GROUPS.filter((g) => g.split).map((g) => (
                <React.Fragment key={`sub-${g.key}`}>
                  {shownCats.map((t, ci) => (
                    <TableCell
                      key={`${g.key}-${t.key}`}
                      align="right"
                      title={t.label}
                      sx={{
                        ...SUBHEAD_SX,
                        padding: '4px 6px',
                        borderTop: `2px solid ${t.border}`,
                        borderLeft: ci === 0 ? '1px solid #ffffff33' : 'none',
                      }}
                    >
                      {t.short}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      ...SUBHEAD_SX,
                      padding: '4px 6px',
                      backgroundColor: '#1e293b',
                      borderTop: `2px solid ${g.color}`,
                      '&.MuiTableCell-stickyHeader': { backgroundColor: '#1e293b', color: '#fff' },
                    }}
                  >
                    Total
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {byMonth.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2 * shownCats.length + 6} sx={{ textAlign: 'center', py: 5, color: '#94a3b8', fontSize: '13px' }}>
                  No invoices found.
                </TableCell>
              </TableRow>
            ) : (
              byMonth.map((m, i) => {
                const stripe = i % 2 ? '#f8faff' : '#fff';
                const cell = (key, value, colour, onClick, extra = {}) => (
                  <TableCell
                    key={key}
                    onClick={value > 0 ? onClick : undefined}
                    title={value > 0 ? `${formatINR(value)} — click for the detail` : undefined}
                    sx={{
                      ...NUM_SX,
                      fontSize: '11.5px',
                      padding: '2px 6px',
                      color: value > 0 ? colour : '#dbe2ea',
                      fontWeight: value > 0 ? 700 : 400,
                      cursor: value > 0 ? 'pointer' : 'default',
                      '&:hover': value > 0 ? { backgroundColor: '#dbeafe' } : {},
                      ...extra,
                    }}
                  >
                    {value > 0 ? compactNum(value) : '—'}
                  </TableCell>
                );
                return (
                  <TableRow key={m.key} sx={{ backgroundColor: stripe, '&:hover': { backgroundColor: '#f0f7ff' } }}>
                    <TableCell
                      sx={{
                        fontSize: '11.5px',
                        fontWeight: 700,
                        color: '#1e293b',
                        whiteSpace: 'nowrap',
                        padding: '2px 8px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: stripe,
                        borderRight: '1px solid #e2e8f0',
                      }}
                    >
                      {m.label}
                    </TableCell>

                    {GROUPS.map((g) =>
                      g.split
                        ? [
                            ...shownCats.map((t, ci) => {
                              const c = m.byCat.get(t.key);
                              return cell(`${m.key}-${g.key}-${t.key}`, c ? c[g.key] : 0, g.color, () => drill(g.key, m, t.key), {
                                borderLeft: ci === 0 ? '2px solid #e2e8f0' : '1px solid #f1f5f9',
                              });
                            }),
                            cell(`${m.key}-${g.key}-total`, m[g.key], g.color, () => drill(g.key, m, null), {
                              backgroundColor: '#f1f5f9',
                              borderLeft: '1px solid #cbd5e1',
                              fontSize: '12px',
                              fontWeight: 800,
                            }),
                          ]
                        : cell(`${m.key}-${g.key}`, m[g.key], g.color, () => drill(g.key, m, null), {
                            borderLeft: '2px solid #e2e8f0',
                          })
                    )}

                    {cell(`${m.key}-clients`, m.payClients.size, '#1e40af', () => drill('received', m, null), {
                      borderLeft: '2px solid #e2e8f0',
                    })}
                    {cell(`${m.key}-invoices`, m.paidInvoices.size, '#1e40af', () => drill('received', m, null))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {byMonth.length > 0 && (
            <TableBody>
              <TableRow>
                <TableCell sx={{ ...FOOT_SX, padding: '5px 8px', position: 'sticky', left: 0, zIndex: 3 }}>
                  All months
                </TableCell>
                {GROUPS.map((g) =>
                  g.split
                    ? [
                        ...shownCats.map((t, ci) => {
                          const v = catTotals.get(t.key)?.[g.key] ?? 0;
                          return (
                            <TableCell
                              key={`tot-${g.key}-${t.key}`}
                              title={formatINR(v)}
                              sx={{
                                ...FOOT_SX,
                                padding: '5px 6px',
                                textAlign: 'right',
                                color: v > 0 ? g.color : '#94a3b8',
                                borderLeft: ci === 0 ? '2px solid #cbd5e1' : '1px solid #dbe2ea',
                              }}
                            >
                              {v > 0 ? compactNum(v) : '—'}
                            </TableCell>
                          );
                        }),
                        <TableCell
                          key={`tot-${g.key}-total`}
                          title={formatINR(monthTotals[g.key])}
                          sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right', color: g.color, backgroundColor: '#dbe2ea' }}
                        >
                          {compactNum(monthTotals[g.key])}
                        </TableCell>,
                      ]
                    : (
                        <TableCell
                          key={`tot-${g.key}`}
                          title={formatINR(monthTotals[g.key])}
                          sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right', color: g.color, borderLeft: '2px solid #cbd5e1' }}
                        >
                          {compactNum(monthTotals[g.key])}
                        </TableCell>
                      )
                )}
                <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right', borderLeft: '2px solid #cbd5e1' }}>
                  {monthTotals.payClients.size}
                </TableCell>
                <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right' }}>{monthTotals.paidInvoices}</TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      </Panel>
      </Box>

      {/* ── bank on the left, payment mode on the right ── */}
      <Box
        sx={{
          display: 'grid',
          // even split: the mode table was taking width the bank and mapped-by
          // tables needed for their own columns
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
          alignItems: 'start',
          gap: 0.6,
          mb: 0.6,
        }}
      >
        {/* left column: the bank split, then who did the mapping */}
        <Box sx={{ display: 'grid', gap: 0.6, minWidth: 0 }}>
          <Panel title="Bank wise collection" hint="₹ in K / L / Cr" pad={false}>
            <SplitTable
              label="Bank"
              maxHeight={200}
              rows={byBank}
              total={receipts.total}
              styleOf={bankStyle}
              onDrill={drillSplit}
            />
          </Panel>
        <Panel
          title="Mapped this month by"
          hint={`${mappedBy.length} ${mappedBy.length === 1 ? 'person' : 'people'}`}
          right={<GroupIcon sx={{ fontSize: 15, color: '#64748b' }} />}
          pad={false}
        >
        <TableContainer sx={{ maxHeight: 200, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={SUBHEAD_SX}>Mapped By</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Payments</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Invoice Lines</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Clients</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Auto</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Manual</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>TDS</TableCell>
                <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Amount Mapped</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedBy.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 2, color: '#94a3b8', fontSize: '12px' }}>
                    Nothing mapped yet this month.
                  </TableCell>
                </TableRow>
              ) : (
                mappedBy.map((u, i) => (
                  <TableRow
                    key={u.who}
                    onClick={() =>
                      openPayments(
                        u.who,
                        `mapped in ${period.label}`,
                        collections.filter(
                          (l) => ((l.created_by ?? '').toString().trim() || 'not recorded') === u.who
                        )
                      )
                    }
                    title="Click for the payment lines"
                    sx={{ cursor: 'pointer', backgroundColor: i % 2 ? '#f8faff' : '#fff', '&:hover': { backgroundColor: '#e8f4fd' } }}
                  >
                    <TableCell sx={{ fontSize: '11.5px', fontWeight: 700, color: '#1e293b', py: 0.35 }}>{u.who}</TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.35 }}>{u.trx.size}</TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.35 }}>{u.lines}</TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.35 }}>{u.clients.size}</TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.35, color: u.auto ? '#7c3aed' : '#cbd5e1' }}>
                      {u.auto || '—'}
                    </TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.35, color: u.manual ? '#1e40af' : '#cbd5e1' }}>
                      {u.manual || '—'}
                    </TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.35, color: '#6d28d9' }}>
                      {u.tds > 0 ? formatINR(u.tds) : '—'}
                    </TableCell>
                    <TableCell sx={{ ...NUM_SX, fontSize: '12.5px', fontWeight: 800, color: '#15803d', py: 0.35 }}>
                      {formatINR(u.amount)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Panel>
        </Box>
        <Panel title="Payment mode wise collection" hint="₹ in K / L / Cr" pad={false}>
          <SplitTable
            label="Mode"
            rows={byMode}
            total={receipts.total}
            styleOf={modeStyle}
            onDrill={drillSplit}
          />
        </Panel>
      </Box>


      {/* ── day by day on the left, client group on the right ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
          alignItems: 'start',
          gap: 0.6,
          mb: 0.6,
        }}
      >
        <Panel
          title="Date wise collection"
          hint={
            byDay.length > 0
              ? `${byDay.length} day${byDay.length === 1 ? '' : 's'} with money in`
              : '₹ in K / L / Cr'
          }
          pad={false}
        >
          <SplitTable
            label="Date"
            rows={byDay}
            total={receipts.total}
            styleOf={dayStyle}
            onDrill={drillSplit}
            maxHeight={300}
            plainLabel
          />
        </Panel>

        <Panel
          title="Client group wise"
          hint={`${byClientGroup.length} group${byClientGroup.length === 1 ? '' : 's'} · ₹ in K / L / Cr`}
          pad={false}
        >
          <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...SUBHEAD_SX, minWidth: 130 }}>Client Group</TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}>Received</TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}>Due</TableCell>
                  <TableCell
                    sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}
                    title="Clients still owing, of the clients in this group"
                  >
                    Owing
                  </TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, padding: '4px 6px', textAlign: 'right' }}>Invoices</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byClientGroup.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: '#94a3b8', fontSize: '12px' }}>
                      No groups in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  byClientGroup.map((g, i) => (
                    <TableRow key={g.key} sx={{ backgroundColor: i % 2 ? '#f8faff' : '#fff' }}>
                      <TableCell
                        title={g.key}
                        sx={{ fontSize: '11.5px', fontWeight: 700, color: '#1e293b', padding: '2px 8px', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {g.key}
                      </TableCell>
                      <TableCell
                        onClick={
                          g.received > 0
                            ? () => openPayments(`${g.key} — received`, `collected in ${period.label}`, g.rows)
                            : undefined
                        }
                        title={g.received > 0 ? `${formatINR(g.received)} — click for the payment lines` : undefined}
                        sx={{
                          ...NUM_SX,
                          fontSize: '11.5px',
                          padding: '2px 6px',
                          fontWeight: 700,
                          color: g.received > 0 ? '#15803d' : '#dbe2ea',
                          cursor: g.received > 0 ? 'pointer' : 'default',
                          '&:hover': g.received > 0 ? { backgroundColor: '#dbeafe' } : {},
                        }}
                      >
                        {g.received > 0 ? compactNum(g.received) : '—'}
                      </TableCell>
                      <TableCell
                        onClick={
                          g.due > 0
                            ? () =>
                                openDue(
                                  `${g.key} — due`,
                                  `${g.invoices} open invoice${g.invoices === 1 ? '' : 's'}`,
                                  invoiceRows.filter(
                                    (r) => (r.group_name ?? '').toString().trim() === g.key && num(r.due) > 0
                                  ),
                                  byInvoiceDateThenClient
                                )
                            : undefined
                        }
                        title={g.due > 0 ? `${formatINR(g.due)} — click for the invoices` : undefined}
                        sx={{
                          ...NUM_SX,
                          fontSize: '11.5px',
                          padding: '2px 6px',
                          fontWeight: 700,
                          color: g.due > 0 ? '#c2410c' : '#dbe2ea',
                          cursor: g.due > 0 ? 'pointer' : 'default',
                          '&:hover': g.due > 0 ? { backgroundColor: '#dbeafe' } : {},
                        }}
                      >
                        {g.due > 0 ? compactNum(g.due) : '—'}
                      </TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', padding: '2px 6px', color: '#475569' }}>
                        {g.owing.size} / {g.clients.size}
                      </TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', padding: '2px 6px', color: '#475569' }}>
                        {g.invoices}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {byClientGroup.length > 1 && (
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ ...FOOT_SX, padding: '5px 8px' }}>All groups</TableCell>
                    <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right', color: '#15803d' }}>
                      {compactNum(byClientGroup.reduce((t, g) => t + g.received, 0))}
                    </TableCell>
                    <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right', color: '#c2410c' }}>
                      {compactNum(byClientGroup.reduce((t, g) => t + g.due, 0))}
                    </TableCell>
                    <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right' }}>
                      {new Set(byClientGroup.flatMap((g) => Array.from(g.owing))).size} /{' '}
                      {new Set(byClientGroup.flatMap((g) => Array.from(g.clients))).size}
                    </TableCell>
                    <TableCell sx={{ ...FOOT_SX, padding: '5px 6px', textAlign: 'right' }}>
                      {byClientGroup.reduce((t, g) => t + g.invoices, 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Panel>
      </Box>

      {/* ── invoice-level detail for whatever outstanding figure was clicked ── */}
      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#1e40af',
            color: '#fff',
            py: 1,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, lineHeight: 1.25 }}>
              {detail?.title}
            </Typography>
            <Typography sx={{ fontSize: '10.5px', opacity: 0.85 }}>
              {[detail?.subtitle, `${(detail?.rows || []).length} rows`].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 800, whiteSpace: 'nowrap' }}>
            {detail?.kind === 'payments'
              ? formatINR((detail?.rows || []).reduce((t, l) => t + num(l.amt ?? l.allocated ?? l.amount), 0))
              : detail?.kind === 'txns'
                ? formatINR(
                    (detail?.rows || []).reduce(
                      (t, r) => t + (r._state === 'PARTIAL' ? Math.max(0, r._amount - r._mapped) : r._amount),
                      0
                    )
                  )
                : formatINR((detail?.rows || []).reduce((t, r) => t + num(r.due), 0))}
          </Typography>
          <IconButton size="small" onClick={() => setDetail(null)} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* one column per fact: if every line shares a mapper, the name belongs
              in the title, not repeated down a column */}
          <TableContainer sx={{ maxHeight: '68vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                {detail?.kind === 'txns' ? (
                  <TableRow>
                    <TableCell sx={SUBHEAD_SX}>Transaction</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Payment Date</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Narration</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Mode</TableCell>
                    <TableCell sx={SUBHEAD_SX}>State</TableCell>
                    <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Mapped</TableCell>
                    <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Yet to map</TableCell>
                    <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Amount</TableCell>
                  </TableRow>
                ) : detail?.kind === 'payments' ? (
                  <TableRow>
                    <TableCell sx={SUBHEAD_SX}>Transaction</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Payment Date</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Client</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Client Type</TableCell>
                    <TableCell sx={SUBHEAD_SX}>Invoice</TableCell>
                    {detail?.showMappedBy && <TableCell sx={SUBHEAD_SX}>Mapped By</TableCell>}
                    <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>TDS</TableCell>
                    <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Amount</TableCell>
                  </TableRow>
                ) : (
                <TableRow>
                  <TableCell sx={SUBHEAD_SX}>Invoice</TableCell>
                  <TableCell sx={SUBHEAD_SX}>Client</TableCell>
                  <TableCell sx={SUBHEAD_SX}>Client Type</TableCell>
                  <TableCell sx={SUBHEAD_SX}>Month</TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Invoice Amount</TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Received</TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }} title="Reduces what is owed">
                    Credit Note
                  </TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }} title="Adds to what is owed">
                    Debit Note
                  </TableCell>
                  <TableCell sx={SUBHEAD_SX}>Bucket</TableCell>
                  <TableCell sx={{ ...SUBHEAD_SX, textAlign: 'right' }}>Outstanding</TableCell>
                </TableRow>
                )}
              </TableHead>
              <TableBody>
                {detail?.kind === 'txns' &&
                  (detail?.rows || []).map((r, i) => {
                    const b = BACKLOG_BY_KEY[r._state];
                    const left = r._state === 'PARTIAL' ? Math.max(0, r._amount - r._mapped) : r._amount;
                    return (
                      <TableRow key={`${r.trx_id ?? i}-${i}`} sx={{ backgroundColor: i % 2 ? '#f8faff' : '#fff' }}>
                        <TableCell sx={{ fontSize: '11.5px', fontFamily: 'monospace', py: 0.4, whiteSpace: 'nowrap' }}>
                          {r.trx_id ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, whiteSpace: 'nowrap' }}>
                          {fmtDay(r.trx_date ?? r.payment_date ?? r.created_at)}
                        </TableCell>
                        <TableCell
                          title={r.description ?? r.trx_description ?? ''}
                          sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {r.description ?? r.trx_description ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '11px', color: '#64748b', py: 0.4, whiteSpace: 'nowrap' }}>
                          {r.trx_mode ?? r.payment_mode ?? '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.4 }}>
                          <Chip
                            size="small"
                            label={b?.label ?? r._state}
                            sx={{ height: 17, fontSize: '9px', fontWeight: 700, backgroundColor: b?.bg, color: b?.color, border: `1px solid ${b?.border}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4, color: r._mapped > 0 ? '#15803d' : '#cbd5e1' }}>
                          {r._mapped > 0 ? formatINR(r._mapped) : '—'}
                        </TableCell>
                        <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4, color: '#b91c1c', fontWeight: 700 }}>
                          {formatINR(left)}
                        </TableCell>
                        <TableCell sx={{ ...NUM_SX, fontSize: '12.5px', fontWeight: 800, color: '#1e293b', py: 0.4 }}>
                          {formatINR(r._amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {detail?.kind === 'payments' &&
                  (detail?.rows || []).map((l, i) => {
                    return (
                      <TableRow key={`${l.trx_id}-${l.invoice_num}-${i}`} sx={{ backgroundColor: i % 2 ? '#f8faff' : '#fff' }}>
                        <TableCell sx={{ fontSize: '11.5px', fontFamily: 'monospace', py: 0.4, whiteSpace: 'nowrap' }}>
                          {l.trx_id ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, whiteSpace: 'nowrap' }}>
                          {fmtDay(l.trx_date ?? l.payment_date ?? l.created_at)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.client_name ?? '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.4 }}>
                          <TierChip tier={categoryOf(l)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '11.5px', fontFamily: 'monospace', color: '#475569', py: 0.4, whiteSpace: 'nowrap' }}>
                          {l.invoice_num ?? l.reference_no ?? '—'}
                        </TableCell>
                        {detail?.showMappedBy && (
                          <TableCell sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, whiteSpace: 'nowrap' }}>
                            {l.created_by ?? '—'}
                          </TableCell>
                        )}
                        <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4, color: '#6d28d9' }}>
                          {num(l.tds_amt ?? l.tds_amount) > 0 ? formatINR(l.tds_amt ?? l.tds_amount) : '—'}
                        </TableCell>
                        <TableCell sx={{ ...NUM_SX, fontSize: '12.5px', fontWeight: 800, color: '#15803d', py: 0.4 }}>
                          {formatINR(l.amt ?? l.allocated ?? l.amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {detail?.kind === 'due' &&
                  (detail?.rows || []).map((r, i) => {
                  const b = AGE_BUCKETS.find((x) => x.key === r._bucket);
                  return (
                    <TableRow
                      key={`${r.reference_no}-${i}`}
                      sx={{ backgroundColor: i % 2 ? '#f8faff' : '#fff', '&:hover': { backgroundColor: '#e8f4fd' } }}
                    >
                      <TableCell sx={{ fontSize: '11.5px', fontFamily: 'monospace', color: '#1e293b', py: 0.4, whiteSpace: 'nowrap' }}>
                        {r.reference_no}
                      </TableCell>
                      <TableCell sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.client_name ?? '—'}
                        {r.unique_id ? (
                          <Typography component="span" sx={{ fontSize: '9.5px', color: '#94a3b8', ml: 0.5, fontFamily: 'monospace' }}>
                            {r.unique_id}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ py: 0.4 }}>
                        <TierChip tier={categoryOf(r)} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '11.5px', color: '#475569', py: 0.4, whiteSpace: 'nowrap' }}>
                        {monthLabel(r._period)}
                      </TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4 }}>{formatINR(r.invoice_amount)}</TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4, color: num(r.prev_payment) > 0 ? '#15803d' : '#cbd5e1' }}>
                        {num(r.prev_payment) > 0 ? formatINR(r.prev_payment) : '—'}
                      </TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4, color: num(r.credit) > 0 ? '#6d28d9' : '#cbd5e1' }}>
                        {num(r.credit) > 0 ? formatINR(r.credit) : '—'}
                      </TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '11.5px', py: 0.4, color: num(r.debit) > 0 ? '#b45309' : '#cbd5e1' }}>
                        {num(r.debit) > 0 ? formatINR(r.debit) : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 0.4 }}>
                        <Chip
                          size="small"
                          label={b?.label}
                          sx={{ height: 17, fontSize: '9px', fontWeight: 700, backgroundColor: b?.bg, color: b?.color, border: `1px solid ${b?.border}` }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...NUM_SX, fontSize: '12.5px', fontWeight: 800, color: '#c2410c', py: 0.4 }}>
                        {formatINR(r.due)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setDetail(null)} sx={{ textTransform: 'none', fontSize: '12px', color: '#64748b' }}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
